import { Box, Flex, Text, chakra } from '@chakra-ui/react';

interface NumberInputProps {
  id?: string;
  name?: string;
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  min?: number;
  max?: number;
  maxLength?: number;
  disabled?: boolean;
  required?: boolean;
  hint?: string;
  error?: string;
}

export function NumberInput({
  id,
  name,
  value,
  onChange,
  label,
  placeholder = '0',
  min = 0,
  max = 100,
  maxLength = 3,
  disabled = false,
  required = false,
  hint,
  error,
}: NumberInputProps) {
  const handleDecrease = () => {
    const currentValue = parseInt(value) || 0;
    if (currentValue > min) {
      onChange(String(currentValue - 1));
    }
  };

  const handleIncrease = () => {
    const currentValue = parseInt(value) || 0;
    if (currentValue < max) {
      onChange(String(currentValue + 1));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const numericValue = e.target.value.replace(/[^0-9]/g, '');
    if (numericValue === '' || parseInt(numericValue) <= max) {
      onChange(numericValue);
    }
  };

  const currentValue = parseInt(value) || 0;
  const isDecreaseDisabled = disabled || currentValue <= min;
  const isIncreaseDisabled = disabled || currentValue >= max;

  return (
    <Box mb={{ base: '4', md: '0' }}>
      {(label || hint) && (
        <Flex justifyContent="space-between" alignItems="center" mb="2">
          {label && (
            <chakra.label htmlFor={id} fontSize={{ base: 'sm', md: 'md' }} fontWeight="medium" color="volyx.text">
              {label}
              {required && ' *'}
            </chakra.label>
          )}
          {hint && (
            <Text as="span" fontSize={{ base: 'xs', md: 'sm' }} color="volyx.textSecondary">
              {hint}
            </Text>
          )}
        </Flex>
      )}
      <Flex position="relative" alignItems="center">
        <chakra.button
          onClick={handleDecrease}
          disabled={isDecreaseDisabled}
          position="absolute"
          zIndex={10}
          left="3"
          top="50%"
          transform="translateY(-50%)"
          w={{ base: '10', md: '12' }}
          h={{ base: '10', md: '12' }}
          display="flex"
          alignItems="center"
          justifyContent="center"
          bg="volyx.lightGray"
          color="volyx.text"
          _hover={{ bg: 'volyx.mediumGray' }}
          borderRadius="lg"
          cursor="pointer"
          transition="colors 0.2s"
          boxShadow="0 1px 3px rgba(0,0,0,0.08)"
          border="none"
          outline="none"
          _focus={{ outline: 'none' }}
          _disabled={{ opacity: 0.6, cursor: 'not-allowed' }}
          aria-label="Decrease"
        >
          <chakra.svg w={{ base: '20px', md: '24px' }} h={{ base: '20px', md: '24px' }} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5 10H15" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
          </chakra.svg>
        </chakra.button>
        <chakra.input
          id={id}
          name={name}
          type="number"
          value={value}
          onChange={handleInputChange}
          placeholder={placeholder}
          w="full"
          h="14"
          bg="volyx.lightGray"
          borderRadius="xl"
          border="none"
          px="14"
          fontSize="md"
          fontFamily="body"
          color="volyx.text"
          textAlign="center"
          boxShadow="0 1px 3px rgba(0,0,0,0.08)"
          transition="all 0.2s"
          _focus={{ outline: 'none', boxShadow: '0 0 0 3px rgba(31,41,50,0.1)', transform: 'scale(1.01)' }}
          _disabled={{ opacity: 0.6, cursor: 'not-allowed' }}
          css={{
            appearance: 'textfield',
            '&::-webkit-outer-spin-button': { appearance: 'none' },
            '&::-webkit-inner-spin-button': { appearance: 'none' },
            MozAppearance: 'textfield',
          }}
          min={min}
          max={max}
          maxLength={maxLength}
          disabled={disabled}
        />
        <chakra.button
          onClick={handleIncrease}
          disabled={isIncreaseDisabled}
          position="absolute"
          zIndex={10}
          right="3"
          top="50%"
          transform="translateY(-50%)"
          w={{ base: '10', md: '12' }}
          h={{ base: '10', md: '12' }}
          display="flex"
          alignItems="center"
          justifyContent="center"
          bg="volyx.lightGray"
          color="volyx.text"
          _hover={{ bg: 'volyx.mediumGray' }}
          borderRadius="lg"
          cursor="pointer"
          transition="colors 0.2s"
          boxShadow="0 1px 3px rgba(0,0,0,0.08)"
          border="none"
          outline="none"
          _focus={{ outline: 'none' }}
          _disabled={{ opacity: 0.6, cursor: 'not-allowed' }}
          aria-label="Increase"
        >
          <chakra.svg w={{ base: '20px', md: '24px' }} h={{ base: '20px', md: '24px' }} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M10 5V15M5 10H15"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </chakra.svg>
        </chakra.button>
      </Flex>
      {error && (
        <Text fontSize="xs" color="volyx.error" mt="1">
          {error}
        </Text>
      )}
    </Box>
  );
}
